'use client';

import { useEffect, useState } from 'react';
import { IconButton, Badge, Stack, Typography } from '@mui/material';
import {
  NotificationsNoneOutlined,
  NotificationsActiveOutlined,
  NotificationsOffOutlined,
} from '@mui/icons-material';

import { useNotificationPermissionStatus } from '@/hooks/useNotificationPermissionStatus';
import { useFCMToken } from '@/hooks/useFCMToken';
import { Or2Dialog } from '@/components/feedback/Or2Dialog';
import { Or2Button } from '@/components/button/Or2Button';
import { useDisplayModeStandalone, useIsIOS } from '@/hooks/usePlatformPWA';
import { usePWAInstallPrompt } from '@/hooks/usePWAInstallPrompt';

export function NotificationsIndicator() {
  const permission = useNotificationPermissionStatus();
  const fcmToken = useFCMToken();
  const isIOS = useIsIOS();
  const isStandalone = useDisplayModeStandalone();
  const [open, setOpen] = useState(false);
  const { deferredPrompt, promptInstall } = usePWAInstallPrompt();

  const enabled = permission === 'granted' && !!fcmToken;
  // Show install UI anywhere a real install prompt is available and the app isn't installed yet
  const canInstall = !isStandalone && Boolean(deferredPrompt);
  const canEnable =
    !enabled && permission !== 'denied' && !(isIOS && !isStandalone);
  // Remove install info in regular desktop browsers; if enabled, we keep UI minimal

  // Auto-prompt for notifications once in standalone if not decided yet
  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (!isStandalone) return;
    if (permission !== 'default') return;
    try {
      const key = 'notifications:auto-prompted';
      if (sessionStorage.getItem(key) === 'true') return;
      (async () => {
        try {
          const res = await Notification.requestPermission();
          sessionStorage.setItem(key, 'true');
          if (res === 'granted') {
            // Ensure app acknowledges immediately
            try {
              window.location.reload();
            } catch {
              // ignore
            }
          }
        } catch {
          // ignore
        }
      })();
    } catch {
      // ignore
    }
  }, [isStandalone, permission]);

  let icon = <NotificationsNoneOutlined />;
  let badgeColor: 'default' | 'error' | 'warning' | 'success' = 'default';
  let showDot = false;
  // For modal content state
  let statusTitle = 'Notifications';
  let helperText: string | null = 'Enable notifications to get alerts.';
  // Keep UI logic simple; actions are rendered conditionally below

  if (enabled) {
    icon = <NotificationsActiveOutlined color="primary" />;
    helperText = 'Notifications are enabled on this device.';
    // no action button when already enabled
  } else if (permission === 'denied') {
    icon = <NotificationsOffOutlined color="disabled" />;
    showDot = true;
    badgeColor = 'error';
    statusTitle = 'Notifications blocked';
    helperText =
      'Notifications are blocked in your browser settings for this site. Please enable them in your browser settings/preferences and reload this page.';
    // no action when blocked
  } else if (isIOS && !isStandalone) {
    icon = <NotificationsNoneOutlined />;
    showDot = true;
    badgeColor = 'warning';
    statusTitle = 'iPhone/iPad setup required';
    helperText = null; // We'll render bespoke steps below
    // Enable flow only after A2HS
  } else if (canEnable) {
    icon = <NotificationsNoneOutlined />;
    showDot = true;
    badgeColor = 'warning';
    statusTitle = 'Enable notifications';
    helperText = isStandalone
      ? 'Enable notifications to get alerts about updates and activity.'
      : 'You can enable notifications without installing. Installing is optional for a better experience.';
  } else if (canInstall) {
    icon = <NotificationsNoneOutlined />;
    showDot = true;
    badgeColor = 'warning';
    statusTitle = 'Install app';
    helperText = 'Install the app for a better experience.';
  } else {
    icon = <NotificationsNoneOutlined />;
    showDot = true;
    badgeColor = 'warning';
    statusTitle = 'Enable notifications';
    helperText = 'Click enable to allow alerts about updates and activity.';
  }

  return (
    <>
      <IconButton
        aria-label="notifications"
        size="small"
        onClick={() => setOpen(true)}
      >
        {showDot ? (
          <Badge variant="dot" color={badgeColor} overlap="circular">
            {icon}
          </Badge>
        ) : (
          icon
        )}
      </IconButton>

      <Or2Dialog
        ariaLabel="notifications"
        title={statusTitle}
        open={open}
        onClose={() => setOpen(false)}
        small
        actions={
          <>
            {/* Enable notifications first when applicable */}
            {canEnable && (
              <Or2Button
                size="small"
                variant="contained"
                onClick={async () => {
                  try {
                    const res = await Notification.requestPermission();
                    if (res === 'granted') {
                      try {
                        window.location.reload();
                      } catch {
                        // ignore
                      }
                    }
                  } catch {
                    // ignore
                  }
                }}
              >
                Enable now
              </Or2Button>
            )}

            {/* Install app as an optional secondary action when prompt is available */}
            {canInstall && (
              <Or2Button size="small" variant="text" onClick={promptInstall}>
                Install app
              </Or2Button>
            )}
          </>
        }
      >
        <Stack spacing={1.5} id="notifications-dialog-description">
          {helperText && <Typography>{helperText}</Typography>}

          {isIOS && !isStandalone && (
            <>
              <Typography fontWeight={600}>
                To enable on iPhone/iPad:
              </Typography>
              <ol style={{ margin: 0, paddingLeft: 20 }}>
                <li>Open this site in Safari.</li>
                <li>Tap the Share icon, then Add to Home Screen.</li>
                <li>Launch the app from your Home Screen.</li>
                <li>Tap the bell icon again to enable notifications.</li>
              </ol>
            </>
          )}

          {permission === 'denied' && (
            <Typography color="text.secondary">
              Tip: In most browsers you can manage this under Site Settings →
              Notifications.
            </Typography>
          )}
        </Stack>
      </Or2Dialog>
    </>
  );
}
